import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useState } from "react";
import { useRedirectBack } from "./-utils";
import type { HTTPError } from "ky";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/http";
import { generateDefaultValue } from "@/lib/utils/zod";
import { CountDown } from "@/components/countdown";
import { Button } from "@/components/ui/button";
import { parseValidationErrors } from "@/lib/utils/backend";

const verifyAccountSearchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/auth/verify-account")({
  component: RouteComponent,
  validateSearch: verifyAccountSearchSchema,
  staticData: {
    title: "Verify Account",
  },
});

const VerifyAccountFormSchema = z.object({
  email: z.email("A valid email is required").min(1, "Email is required"),
  code: z.string("OTP is required"),
});
type VerifyAccountFormSchema = z.infer<typeof VerifyAccountFormSchema>;

function RouteComponent() {
  const searchEmail = Route.useSearch({
    select: (s) => s.email,
  });

  const redirect = useRedirectBack();

  const form = useForm({
    resolver: standardSchemaResolver(VerifyAccountFormSchema),
    defaultValues: {
      ...generateDefaultValue(VerifyAccountFormSchema),
      email: searchEmail?.includes("@") ? searchEmail : "",
    },
  });

  const verifyAccount = useMutation({
    async mutationFn(json: VerifyAccountFormSchema) {
      await api.auth.post("users/verify-email", {
        json,
      });
    },
    onSuccess() {
      redirect({
        initialGreeting: true,
      });
    },
    async onError(error: HTTPError) {
      const data = await error.response.json<{
        detail?: string;
        errors?: Record<string, string>;
      }>();
      if (data.errors) {
        const parsedErrors = parseValidationErrors<VerifyAccountFormSchema>(
          data.errors,
        );
        parsedErrors.forEach(([path, message]) =>
          form.setError(path, {
            type: "validate",
            message,
          }),
        );
      } else if (data.detail) {
        form.setError("root.serverError", {
          type: "custom",
          message: data.detail,
        });
      }
    },
  });

  const [emailResendCooldown, setEmailResendCooldown] = useState<null | Date>(
    null,
  );
  const resendEmail = useMutation({
    async mutationFn(email: string) {
      const res = await api.auth.post("users/resend-email-verification", {
        json: { email },
        throwHttpErrors: false,
        retry: {},
      });
      const data = await res.json<{
        detail: string;
        errors?: Record<string, string>;
      }>();

      if (res.ok) return;
      else if (res.status === 400)
        throw new Error(data.errors?.["('email',)"] ?? data.detail);
      else if (res.status === 429) {
        const retryAfter = res.headers.get("Retry-After");
        if (!retryAfter) throw new Error("Try again later");

        const parsedRetryAfter = parseInt(retryAfter);
        let retryDate: Date;
        if (Number.isNaN(parsedRetryAfter)) {
          retryDate = new Date(retryAfter);
        } else {
          retryDate = new Date();
          retryDate.setSeconds(retryDate.getSeconds() + parsedRetryAfter);
        }

        setEmailResendCooldown(retryDate);
        setTimeout(
          () => setEmailResendCooldown(null),
          retryDate.valueOf() - Date.now(),
        );
      }
    },
    onSuccess() {
      const cooldownDate = new Date();
      cooldownDate.setMinutes(cooldownDate.getMinutes() + 1);
      setEmailResendCooldown(cooldownDate);
      setTimeout(
        () => setEmailResendCooldown(null),
        cooldownDate.valueOf() - Date.now(),
      );
    },
  });
  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-2">
      <div className="w-full space-y-6">
        <div className="space-y-4">
          <h1 className="text-center text-2xl font-semibold">
            We've sent a confirmation code to your email address.
          </h1>
          <h2 className="text-center text-base text-gray-600">
            Please enter the code below to verify your account.
          </h2>

          <Form {...form}>
            <form
              className="space-y-6"
              onSubmit={form.handleSubmit((formData) => {
                if (verifyAccount.isPending) return;
                verifyAccount.mutate(formData);
              })}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* Email</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="email"
                        placeholder="Email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* Confirmation Code</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="one-time-code"
                        placeholder="OTP Code"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <Button type="submit" size="lg" className="w-full py-3">
                  {verifyAccount.isPending ? "Verifying..." : "Verify Account"}
                </Button>
                {form.formState.errors.root?.serverError ? (
                  <p className="mt-2 text-sm text-destructive">
                    {form.formState.errors.root.serverError.message}
                  </p>
                ) : null}
              </div>
              {/* Did not receive code section */}
              <div className="mt-4 flex flex-col items-start text-base">
                <div>
                  Didn’t receive a code?{" "}
                  <button
                    type="button"
                    disabled={emailResendCooldown !== null}
                    className="ml-1 cursor-pointer font-bold text-[#dd2590] not-disabled:hover:underline focus:outline-none disabled:cursor-auto disabled:text-muted-foreground/50"
                    onClick={() => resendEmail.mutate(form.getValues("email"))}
                  >
                    Resend Code
                  </button>
                </div>
                {(emailResendCooldown || resendEmail.isError) && (
                  <div className="mt-1 text-sm text-muted-foreground">
                    {emailResendCooldown ? (
                      <>
                        Please wait <CountDown till={emailResendCooldown} />{" "}
                        before resending
                      </>
                    ) : resendEmail.isError ? (
                      <>{resendEmail.error.message}</>
                    ) : null}
                  </div>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </main>
  );
}
